/**
 * Множественный выбор рабочих мест на дашбордах услуг — общая механика «Спецоценки» и
 * «Профрисков» (centrika-docs!35: «для хранения, восстановления и очистки выбора используется
 * существующая механика множественного выбора страницы „Спецоценка“»). До !35 механика жила
 * прямо в `pages/sout-dashboard.tsx`; здесь она вынесена без изменений поведения, чтобы у двух
 * страниц был один код, а не две расходящиеся копии.
 *
 * Персистентность (§8 p3-sout-workplaces, tasks.md 4.3a): `sessionStorage` под ключом СТРАНИЦЫ
 * (у каждого дашборда свой — маршруты `/sout` и `/profrisks` единственные и без параметров,
 * общий ключ на вкладку достаточен). Выбор переживает переход в карточку рабочего места или
 * сотрудника и возврат «Назад», не переживает закрытие вкладки — вкладка не должна становиться
 * «зависшим черновиком выбора» навсегда. Ошибки доступа к `sessionStorage` (приватный режим и
 * т.п.) страницу не роняют: выбор просто не переживёт переход.
 */
import { useCallback, useEffect, useState } from 'react'

function readStoredSelection(key: string): Set<string> {
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return new Set()
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return new Set()
    return new Set(parsed.filter((v): v is string => typeof v === 'string'))
  } catch {
    return new Set()
  }
}

function writeStoredSelection(key: string, ids: Set<string>): void {
  try {
    if (ids.size === 0) sessionStorage.removeItem(key)
    else sessionStorage.setItem(key, JSON.stringify(Array.from(ids)))
  } catch {
    // недоступно (приватный режим и т.п.) — не критично, просто не персистится
  }
}

export interface WorkplaceSelection {
  /** Текущий выбор — набор `workplaceId`. */
  selected: Set<string>
  /** Отметить/снять одно место (повторное нажатие снимает выбор). */
  toggle: (workplaceId: string) => void
  /** Очистить выбор целиком (после создания заказа — своё дело он сделал). */
  clear: () => void
  /**
   * Сверить восстановленный выбор со свежими данными: id, которых нет среди `selectable`,
   * тихо отбрасываются. Место могло пропасть из структуры или перестать быть выбираемым, и
   * тянуть такой id в счётчик и в запрос добавления нельзя. Ссылка стабильна — функцию можно
   * держать в зависимостях эффекта.
   */
  reconcile: (selectable: Set<string>) => void
}

export function useWorkplaceSelection(storageKey: string): WorkplaceSelection {
  const [selected, setSelected] = useState<Set<string>>(() => readStoredSelection(storageKey))

  // Персистируем при каждом изменении (в т.ч. очистку после создания заказа).
  useEffect(() => {
    writeStoredSelection(storageKey, selected)
  }, [storageKey, selected])

  const toggle = useCallback((workplaceId: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(workplaceId)) next.delete(workplaceId)
      else next.add(workplaceId)
      return next
    })
  }, [])

  const clear = useCallback(() => setSelected(new Set()), [])

  const reconcile = useCallback((selectable: Set<string>) => {
    setSelected((prev) => {
      if (prev.size === 0) return prev
      let changed = false
      const next = new Set<string>()
      for (const id of prev) {
        if (selectable.has(id)) next.add(id)
        else changed = true
      }
      return changed ? next : prev
    })
  }, [])

  return { selected, toggle, clear, reconcile }
}
